import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Text } from '@/components/Typography';
import { useLocation, useParams } from 'react-router-dom';
import { Form } from 'antd';
import { useHistory } from 'react-router-dom';

import { States, Context, Methods, FormStep, LibertyMutualRouteParams } from './types';
import { InitialValuesMapper } from '../hooks/services/InitialValuesMapper';
import { useApplicationContext } from '@/context/v1/Application/context';
import { LibertyMutualReportDTO } from '../hooks/types/response';
import { Informations } from '../Report/Informations';
import { useGetReport, useGetResult } from '../hooks';
import { ReportResult } from '../Report/ReportResult';
import { LoadingSkeleton } from '../LoadingSkeleton';
import { TaskList } from '../Report/TaskList';
import { useSandboxExistsReport } from '@/hooks/useSandbox/useSandboxExistsReport';
import { SendReportRequest } from '@/types/Sandbox';


type LibertyMutualProviderProps = {
	children: ReactNode;
};

const { useForm } = Form;
const LibertyMutualContext = createContext<Context>({} as Context);

const steps: FormStep[] = [
	{
		title: 'Informations',
		component: <Informations />
	},
	{
		title: 'TaskList',
		component: <TaskList />
	},
	{
		title: 'Result',
		component: <ReportResult />
	}
];

export function LibertyMutualProvider({ children }: LibertyMutualProviderProps) {
	const history = useHistory();
	const [form] = useForm();
	const { search } = useLocation();
	const searchParams = new URLSearchParams(search);
	const { file_id } = useParams<LibertyMutualRouteParams>();
	const { organization, company, sendReport, isSandbox } = useApplicationContext();
	const custom_report = !!searchParams.get('custom_report');
	const [updatedLibertyMutual, setUpdatedLibertyMutual] = useState<LibertyMutualReportDTO | undefined>(undefined);
	const [isSendReportModalOpen, setIsSendReportModalOpen] = useState<boolean>(false);

	const {
		data: libertyMutual,
		isFetching: fetchingReport,
		isError: errorGettingReport
	} = useGetReport({
		organization_id: organization?.id,
		company_id: company?.id,
		file_id
	});
	const {
		data: libertyMutualResult,
		isInitialLoading: fetchingResult,
		isError: errorGettingResult
	} = useGetResult({
		organization_id: organization?.id,
		company_id: company?.id,
		file_id,
		isLastStep: libertyMutual.step === 2
	});

	const initialValuesMapper = new InitialValuesMapper(libertyMutual);
	const initialValues = initialValuesMapper.mapInitialValues();

	const decimalSeparatorMapper = {
		metric: ',',
		imperial: '.'
	};

	function handleSendReport(): void {
		const request: SendReportRequest = {
			report_type: 'LIBERTY_MUTUAL',
			id: libertyMutual?.id || '',
			company_id: company?.id,
			organization_id: organization?.id
		};
		setIsSendReportModalOpen(false);
		sendReport(request, '/reporting');

		const currentPath = history.location.pathname;
		const cleanPath = currentPath.match(/(\/reporting\/liberty-mutual)/)?.[1];

		if (cleanPath && currentPath !== cleanPath) {
			history.replace(cleanPath);
		}
	}

	function useExistsReport(
		isSandbox: boolean,
		file_id: string | undefined,
		company_id: string | undefined,
		organization_id: string | undefined
	): { exists: boolean, isLoading: boolean } {
		const { data, isLoading } = useSandboxExistsReport(
			{
				file_id: file_id ?? '',
				company_id: company_id ?? '',
				organization_id: organization_id ?? '',
				report_type: 'LIBERTY_MUTUAL'
			},
			{
				enabled: Boolean(isSandbox && file_id && company_id && organization_id),
			}
		);

		if (isLoading) return { exists: false, isLoading };

		return {
			exists: data?.exists ?? false,
			isLoading
		};
	}

	function handleOpenSendReportModal(): void {
		setIsSendReportModalOpen(true);
	}

	function handleCloseSendReportModal(): void {
		setIsSendReportModalOpen(false);
	}

	function handleUpdateReport(report: LibertyMutualReportDTO) {
		setUpdatedLibertyMutual(report);
	}

	const loading = fetchingReport || fetchingResult;

	if (errorGettingResult) {
		return <Text>Oops... Something went wrong!</Text>;
	}

	if (loading) {
		return <LoadingSkeleton loading={loading} />;
	}

	const states: States = {
		steps,
		file_id,
		custom_report,
		errorGettingReport,
		libertyMutualResult,
		gettingReport: loading,
		libertyMutual: updatedLibertyMutual ?? libertyMutual,
		decimalSeparator: decimalSeparatorMapper[libertyMutual?.system_of_units?.name],
		isSendReportModalOpen,
		isSandbox
	};

	const methods: Methods = {
		handleUpdateReport,
		handleSendReport,
		useExistsReport,
		handleOpenSendReportModal,
		handleCloseSendReportModal
	};

	const context: Context = {
		...states,
		...methods
	};

	return (
		<LibertyMutualContext.Provider value={context}>
			<Form form={form} initialValues={initialValues}>
				{children}
			</Form>
		</LibertyMutualContext.Provider>
	);
}

export function useLibertyMutualContext() {
	const context = useContext(LibertyMutualContext);
	return context;
}
